export interface ListingRef {
  /** Composite listing id(s) this block is bound to (single/multiple/custom). */
  ids?: string | Array<string | undefined | null>;
  /**
   * Stable per-instance identity for a criteria block, generated once and
   * persisted in its JSON (see CriteriaBlock/index.tsx's getCriteriaBlockId).
   * The backend looks the actual criteriaConfig up by this id instead of
   * reading it off the HTML - same shape as data-listing-id, just keyed by a
   * generated id instead of the MLS composite id (a criteria block has no
   * natural id of its own to bind on).
   */
  criteriaId?: string;
}

function sanitizeIdx(idx: string): string {
  return String(idx || '').replace(/[^a-zA-Z0-9_-]/g, '_');
}

/** The stable per-instance id used, for blocks that compile through real MJML
 * (e.g. the Custom Listing Wrapper), as the css-class an mj-selector matches
 * against to re-attach data-listing-id onto that same compiled element. */
export function getListingClassId(idx: string | null | undefined): string {
  return `lst_${sanitizeIdx(idx || '')}`;
}

// `ids` is pipe-delimited (e.g. "3618873|mls_ecar|active|") and never carries
// a quote, so it's kept plain/readable — quoted only as defense in depth.
function escapeAttr(value: string): string {
  return String(value || '').replace(/"/g, '&quot;');
}

/**
 * The binding as a real HTML attribute, for splicing directly onto a block's
 * own compiled root element - the same treatment Image/index.tsx gives
 * data-dynamic-tag on the <img> itself. Returns '' (nothing to splice in)
 * when there's no binding.
 */
export function buildListingDataAttr(ref?: ListingRef): string {
  if (!ref) return '';
  if (ref.ids) {
    const ids = (Array.isArray(ref.ids) ? ref.ids : [ref.ids]).filter(Boolean).join(',');
    return ids ? ` data-listing-id="${escapeAttr(ids)}"` : '';
  }
  if (ref.criteriaId) {
    return ` data-listing-criteria-id="${escapeAttr(ref.criteriaId)}"`;
  }
  return '';
}
